import useQuery from '@/api/queries/useQuery';
import { usePartnerScoreFilter, UsePartnerScoreFilterReturn } from '@/pages/PartnerScore/components/PartnerScoreFilter';
import { CubeQuery } from '@/types/cube';
import { UseQueryResult } from '@tanstack/react-query';
import { PartnerScoreCacheKeys } from '../../cacheKeys';

import { usePartnerScoreGlobalFilter } from '@/components/partner';
import { verifyRequestedPeriodHasData } from '@/components/partner/utils/partnerScoreUtils';
import { toNumberOrNull } from '@/utils/cube/mapper';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { PARTNERSCORE_CUBE_NAME } from '@api/hcp';
import { AxiosError } from 'axios';
import { sortByYearQuarter } from '../../utils';

interface UseGetAverageCSATScoreParams extends Pick<UsePartnerScoreFilterReturn, 'year' | 'quarter'> {
  surveyIdList?: UsePartnerScoreFilterReturn['surveyIdList'];
  hcpCountryCode?: string;
}

export type UseGetAverageCSATScoreResponse = {
  currentAverage: number | null;
  change: number | null;
};

type PeriodRecordType = {
  year: number;
  quarter: number;
  total: number;
  positiveAnswers: number;
  otherAnswers: number;
};

function useGetAverageCSATScore<TData = UseGetAverageCSATScoreResponse, TError = AxiosError>({
  year,
  quarter,
  surveyIdList = [],
  hcpCountryCode,
}: UseGetAverageCSATScoreParams): UseQueryResult<TData, TError> {
  const { globalFiltersWithoutHcpCountryCode, countries } = usePartnerScoreGlobalFilter();
  const { filtersWithImmediatePreviousPeriods, filterQuarterDimensions, filterSurveyList } = usePartnerScoreFilter();

  const selectedYear = year ?? 0;
  const selectedQuarter = quarter ?? 0;

  const periodFilters =
    filtersWithImmediatePreviousPeriods?.({
      cubeNameArg: PARTNERSCORE_CUBE_NAME,
      currentYear: selectedYear,
      currentQuarter: selectedQuarter,
    }) ?? [];

  const baseDimensions = [
    `${PARTNERSCORE_CUBE_NAME}.quarter`,
    `${PARTNERSCORE_CUBE_NAME}.year`,
    `${PARTNERSCORE_CUBE_NAME}.trans_csat_q12`,
  ];

  const dimensions = filterQuarterDimensions?.(baseDimensions, selectedQuarter) ?? baseDimensions;

  const query: CubeQuery = {
    measures: [`${PARTNERSCORE_CUBE_NAME}.countCesScoreCsat`],
    dimensions,
    filters: [
      ...periodFilters,
      {
        member: `${PARTNERSCORE_CUBE_NAME}.country_code`,
        operator: 'equals',
        values: hcpCountryCode ? [hcpCountryCode] : countries,
      },
      {
        member: `${PARTNERSCORE_CUBE_NAME}.trans_csat_q12`,
        operator: 'set',
      },
      {
        member: `${PARTNERSCORE_CUBE_NAME}.trans_csat_q12`,
        operator: 'notEquals',
        values: ['6'],
      },
      ...(filterSurveyList?.(PARTNERSCORE_CUBE_NAME, surveyIdList ?? []) ?? []),
      globalFiltersWithoutHcpCountryCode,
    ],
    order: { [`${PARTNERSCORE_CUBE_NAME}.year`]: 'desc', [`${PARTNERSCORE_CUBE_NAME}.quarter`]: 'desc' },
    limit: 100,
  };

  const isEnabled = (!!countries.length || !!hcpCountryCode) && !!year && !!quarter;

  return useQuery<CubeRawRow[], TError, TData>({
    payload: query,
    options: {
      queryKey: [...PartnerScoreCacheKeys.averageCustomerSatisfactionScore(query)],
      select: (data): TData => {
        if (!data.length || !verifyRequestedPeriodHasData(data, year)) {
          return { change: null, currentAverage: null } as TData;
        }

        const sortedData = sortByYearQuarter(
          data.map(dataItem => ({
            year: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.year`]) ?? 0,
            quarter: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.quarter`]) ?? 0,
            countCesScoreCsat: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.countCesScoreCsat`]) ?? 0,
            score: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.trans_csat_q12`]) ?? 0,
          })),
          selectedYear,
          selectedQuarter,
        );

        const periodRecord = new Map<string, PeriodRecordType>();

        sortedData.forEach(sortedDataItem => {
          const period = `${sortedDataItem.year} ${sortedDataItem.quarter}`;
          const existingPeriod = periodRecord.get(period);

          if (existingPeriod) {
            existingPeriod[sortedDataItem.score < 4 ? 'otherAnswers' : 'positiveAnswers'] +=
              sortedDataItem.countCesScoreCsat;
            existingPeriod.total += sortedDataItem.countCesScoreCsat;
          } else {
            periodRecord.set(period, {
              year: sortedDataItem.year,
              quarter: sortedDataItem.quarter,
              total: sortedDataItem.countCesScoreCsat,
              positiveAnswers:
                sortedDataItem.score === 4 || sortedDataItem.score === 5 ? sortedDataItem.countCesScoreCsat : 0,
              otherAnswers: sortedDataItem.score < 4 ? sortedDataItem.countCesScoreCsat : 0,
            });
          }
        });

        const [current, previous] = Array.from(periodRecord.values());

        const calculateAverage = (record: PeriodRecordType) => {
          if (!record) {
            return null;
          }
          return +((record.positiveAnswers / record.total) * 100).toFixed();
        };

        const currentAverage = calculateAverage(current);
        const previousAverage = calculateAverage(previous);
        const change = previousAverage && currentAverage !== null ? currentAverage - previousAverage : null;

        return {
          currentAverage,
          change,
        } as TData;
      },
      enabled: isEnabled,
    },
  });
}

export default useGetAverageCSATScore;
